import { useEffect, useState } from 'react';
import { Heading4, SectionTitle } from '../../../components/typography/Heading';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import List from '../../../components/text/List';
import { setupVippsRecurringPaymentWithoutTransaction } from '../../../payment/vipps-actions';
import { useDispatch, useSelector } from 'react-redux';
import { hasErrorsOnAmount } from '../../../payment/payment-model';

import styles from './give-recurring-support.module.less';
import ScreenReaderOnly from '../../../accessibility/ScreenReaderOnly';
import ToggleButton from '../../../components/toggle-button/ToggleButton';
import { formatMoneyKr, formatOrganizationNumber } from '../../../utils';
import VippsButton from '../../../components/button/vipps-button/VippsButton';
import RoundedImage from '../../../components/RoundedImage';
import Expandable from '../../../components/expandable/Expandable';
import { Link } from 'react-router-dom';
import {
    OrganizationTheme,
    SAND_SECONDARY_ORGANIZATION_COLOR,
    WHITE_ORGANIZATION_COLOR,
} from '../../../organization/theme/OrganizationTheme';
import { InputTextLike } from '@sb1/ffe-form-react';

const TRACKING_CATEGORY = 'support';
const STANDALONE_RECURRING_GIVER_TRACKING_NAME = 'standalone-give-recurring';

const predefinedAmountsDefault = [
    {
        amount: 200,
        text: '',
    },
    {
        amount: 300,
        text: '',
    },
    {
        amount: 400,
        text: '',
    },
    {
        amount: 500,
        text: '',
    },
];

const GiveRecurringPaymentForOrgPage = ({
    projectId,
    mock,
    predefinedAmounts = predefinedAmountsDefault,
    initialAmount,
    title,
    text,
    imageUrl,
    organizationId,
    organizationName = 'organisasjonen',
    organizationNumber,
}) => {
    const dispatch = useDispatch();
    const minAmount = useSelector((state) => state.paymentConfig.giveMinAmount);
    const maxAmount = useSelector((state) => state.paymentConfig.giveMaxAmount);
    const [amount, setAmount] = useState(null);

    useEffect(() => {
        setAmount(initialAmount);
    }, [initialAmount]);

    useEffect(() => {
        if (!mock) {
            trackEvent(TRACKING_CATEGORY, STANDALONE_RECURRING_GIVER_TRACKING_NAME, {
                'project-id': projectId,
                action: 'show',
            });
        }
    }, []);

    const giveAmountChangeHandler = (event) => {
        let amount = event.target.value.replace(/[^0-9.]/g, '').match(/^[0-9]+/);
        if (amount) {
            amount = parseInt(amount, 10);
        }
        setAmount(amount);
    };

    const inputHasError = () => {
        const inputError = hasErrorsOnAmount({
            amount: amount,
            giveMinAmount: minAmount,
            giveMaxAmount: maxAmount,
        });
        return !!inputError;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!mock) {
            const hasInputError = inputHasError();
            if (!hasInputError) {
                trackEvent(TRACKING_CATEGORY, STANDALONE_RECURRING_GIVER_TRACKING_NAME, {
                    'project-id': projectId,
                    'organization-id': organizationId,
                    action: 'submit',
                });
                dispatch(
                    setupVippsRecurringPaymentWithoutTransaction({
                        amount: amount,
                        organizationId: organizationId,
                    })
                );
            }
        }
    };

    const predefinedAmountContainerClass =
        predefinedAmounts.filter((predefinedAmount) => predefinedAmount.amount).length % 2 === 0
            ? styles.evenNumberOfButtons
            : styles.oddNumberOfButtons;
    const predefinedAmountButtonClass = [styles.predefinedAmountButton, WHITE_ORGANIZATION_COLOR].join(' ');

    return (
        <OrganizationTheme className={styles.container} theme={SAND_SECONDARY_ORGANIZATION_COLOR}>
            {imageUrl && <img src={imageUrl} className={styles.compactTopImage} alt="Organisasjonens bilde" />}
            <SectionTitle className={styles.title}>
                {title || `Vil du bli fast giver i ${organizationName}?`}
            </SectionTitle>
            <p>{text || 'Ved å bli fast giver hjelper du oss med å fortsette med det viktige arbeidet.'}</p>
            <OrganizationTheme className={styles.compactContainer} theme={SAND_SECONDARY_ORGANIZATION_COLOR}>
                <div className={styles.becomeRecurringGiver}>
                    {imageUrl && <img src={imageUrl} className={styles.compactImage} alt="Organisasjonens bilde" />}
                    <Heading4 className={styles.amountHeading}>Velg ønsket månedsbeløp</Heading4>
                    <form className={styles.recurringPaymentWrapper} onSubmit={onSubmit}>
                        <div>
                            <fieldset aria-labelledby="suggested-amounts">
                                <ScreenReaderOnly tag={'legend'} id="suggested-amounts">
                                    Foreslåtte beløp
                                </ScreenReaderOnly>
                                <div
                                    className={[
                                        styles.predefinedAmountButtonContainer,
                                        predefinedAmountContainerClass,
                                    ].join(' ')}
                                >
                                    {predefinedAmounts.map((predefinedAmount, index) => {
                                        if (!predefinedAmount?.amount) {
                                            return null;
                                        }
                                        return (
                                            <ToggleButton
                                                key={index}
                                                selected={amount === predefinedAmount?.amount}
                                                className={predefinedAmountButtonClass}
                                                onClick={() => {
                                                    setAmount(predefinedAmount?.amount);
                                                }}
                                            >
                                                {formatMoneyKr(predefinedAmount?.amount)}
                                            </ToggleButton>
                                        );
                                    })}
                                </div>
                                <div className={styles.inputAmountWrapper}>
                                    <InputTextLike
                                        className={styles.inputAmount}
                                        aria-labelledby="inputAmountLabel"
                                        onChange={giveAmountChangeHandler}
                                        onBlur={(e) => giveAmountChangeHandler(e, true)}
                                        value={amount || ''}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        aria-describedby="inputAmountFormError"
                                        ariaLabel={'Velg ønsket månedsbeløp'}
                                    />
                                    <div className={styles.inputAmountPostfix}>kr</div>
                                </div>
                                <div>
                                    {predefinedAmounts.map((predefinedAmount, index) => {
                                        if (!predefinedAmount.text || !predefinedAmount.amount) {
                                            return null;
                                        }
                                        if (predefinedAmount?.amount === amount) {
                                            return (
                                                <div key={index} className={styles.predefinedAmountTextContainer}>
                                                    <div>
                                                        <RoundedImage
                                                            src="/public/images/hjerte-uten-bg.svg"
                                                            className={styles.predefinedAmountImage}
                                                        />
                                                    </div>
                                                    <div className={styles.predefinedAmountText}>
                                                        {predefinedAmount.text}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </fieldset>
                        </div>
                        <div className={styles.buttonsContainer}>
                            <VippsButton
                                onClick={onSubmit}
                                leaderText="Fortsett med "
                                className={styles.initiateRecurringPaymentButton}
                            />
                        </div>
                        <div className={styles.orgInfo}>
                            {organizationName}, org.nr: {formatOrganizationNumber(organizationNumber)}
                        </div>
                        {!mock && (
                            <Expandable
                                className={styles.compactInfo}
                                childrenClassName={styles.compactInfoContent}
                                title={
                                    <span className={styles.compactInfoHeading}>
                                        Ved å bli fast giver aksepterer du Spleis sine{' '}
                                        <Link to="/terms">vilkår for bruk</Link> og{' '}
                                        <Link to="/personvern">personvernerklæring</Link>. Dine kontaktopplysninger vil
                                        også deles med {organizationName}.
                                    </span>
                                }
                            >
                                <p>Følgende opplysninger deles med mottakerorganisasjonen:</p>
                                <List className={styles.recurringPaymentInfoPersonalDataInfoList}>
                                    <li>Navnet ditt</li>
                                    <li>E-postadresssen din</li>
                                    <li>Telefonnummeret ditt</li>
                                </List>
                                <p>Mottakerorganisasjonen er ansvarlig for behandlingen av dine personopplysninger.</p>
                                <p>
                                    Fremtidige trekk kan når som helst endres fra Vipps-appen, eller ved å kontakte{' '}
                                    <Link to="mailto:hei@spleis.no">hei@spleis.no</Link>
                                </p>
                            </Expandable>
                        )}
                    </form>
                </div>
            </OrganizationTheme>
        </OrganizationTheme>
    );
};

export default GiveRecurringPaymentForOrgPage;
